"use client";

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { MultiSelectEnhanced } from "@/components/ui/multi-select-enhanced";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AVAILABLE_LANGUAGES } from "@/lib/constants/languages";
import { FaqVisibilitySelect } from "@/components/admin/faqs/faq-visibility-select";

interface VenueFAQ {
  faq_id: number;
  venue_id: number;
  questions: Record<string, string>;
  answers: Record<string, string>;
  display_order: number;
  status: { id: number; name: string };
  created_at: string;
  updated_at: string;
}

interface ShowFAQ {
  faq_id: number;
  visibility: string;
  /** Each language has its own link, so its own visibility. */
  visibilities?: Record<string, string>;
  question: string;
  answer: string;
}

interface ShowFAQsTabProps {
  showId: string;
  venueId: string;
  availableLanguages: string[];
}

export function ShowFAQsTab({
  showId,
  venueId,
  availableLanguages,
}: ShowFAQsTabProps) {
  const [venueFaqs, setVenueFaqs] = useState<VenueFAQ[]>([]);
  const [showFaqs, setShowFaqs] = useState<ShowFAQ[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeLanguage, setActiveLanguage] = useState(
    availableLanguages[0] || "en",
  );
  const [selectedFaqIds, setSelectedFaqIds] = useState<number[]>([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [faqToDelete, setFaqToDelete] = useState<number | null>(null);

  const fetchVenueFAQs = async () => {
    try {
      const response = await fetch(`/api/venues/${venueId}/faqs`);
      const data = await response.json();

      if (data.success) {
        setVenueFaqs(data.data || []);
      } else {
        toast.error("Failed to load venue FAQs");
      }
    } catch (error) {
      console.error("Error fetching venue FAQs:", error);
      toast.error("Failed to load venue FAQs");
    }
  };

  const fetchShowFAQs = async () => {
    try {
      const response = await fetch(
        `/api/venues/${venueId}/shows/${showId}/faqs`,
      );
      const data = await response.json();

      if (data.success) {
        setShowFaqs(data.data || []);
        setSelectedFaqIds((data.data || []).map((faq: ShowFAQ) => faq.faq_id));
      } else {
        // Show FAQs API might not exist yet, that's okay
        setShowFaqs([]);
        setSelectedFaqIds([]);
      }
    } catch (error) {
      console.error("Error fetching show FAQs:", error);
      // Don't show error for missing show FAQs API
      setShowFaqs([]);
      setSelectedFaqIds([]);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchVenueFAQs(), fetchShowFAQs()]);
      setLoading(false);
    };
    loadData();
  }, [showId, venueId]);

  const handleFAQSelection = async (newSelectedIds: number[]) => {
    try {
      // Save to database
      const response = await fetch(
        `/api/venues/${venueId}/shows/${showId}/faqs`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            faq_ids: newSelectedIds,
          }),
        },
      );

      const data = await response.json();

      if (!data.success) {
        toast.error(data.error || "Failed to update FAQ selection");
        return;
      }

      // Update local state after successful API call
      setSelectedFaqIds(newSelectedIds);

      // Create show FAQ entries for display, preserving existing visibility
      const newShowFaqs = newSelectedIds.map((faqId) => {
        const venueFaq = venueFaqs.find((f) => f.faq_id === faqId);
        const existingShowFaq = showFaqs.find((f) => f.faq_id === faqId);
        return {
          faq_id: faqId,
          visibility: existingShowFaq?.visibility || "visible", // Preserve existing visibility
          visibilities: existingShowFaq?.visibilities,
          question:
            venueFaq?.questions[activeLanguage] ||
            venueFaq?.questions["en"] ||
            "",
          answer:
            venueFaq?.answers[activeLanguage] || venueFaq?.answers["en"] || "",
        };
      });

      setShowFaqs(newShowFaqs);
      toast.success("FAQ selection updated");
    } catch (error) {
      console.error("Error updating FAQ selection:", error);
      toast.error("Failed to update FAQ selection");
    }
  };

  const handleVisibilityChange = async (
    faqId: number,
    newVisibility: string,
  ) => {
    try {
      // Save to database
      const response = await fetch(
        `/api/venues/${venueId}/shows/${showId}/faqs/${faqId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            visibility: newVisibility,
            lang: activeLanguage,
          }),
        },
      );

      const data = await response.json();

      if (!data.success) {
        toast.error(data.error || "Failed to update visibility");
        return;
      }

      // Update local state after successful API call
      setShowFaqs((prev) =>
        prev.map((faq) =>
          faq.faq_id === faqId
            ? {
                ...faq,
                visibilities: {
                  ...faq.visibilities,
                  [activeLanguage]: newVisibility,
                },
              }
            : faq,
        ),
      );

      toast.success("Visibility updated");
    } catch (error) {
      console.error("Error updating visibility:", error);
      toast.error("Failed to update visibility");
    }
  };

  const handleDeleteClick = (faqId: number) => {
    setFaqToDelete(faqId);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (faqToDelete === null) return;

    try {
      // Remove from database
      const response = await fetch(
        `/api/venues/${venueId}/shows/${showId}/faqs/${faqToDelete}`,
        {
          method: "DELETE",
        },
      );

      const data = await response.json();

      if (!data.success) {
        toast.error(data.error || "Failed to remove FAQ");
        return;
      }

      // Update local state after successful API call
      setSelectedFaqIds((prev) => prev.filter((id) => id !== faqToDelete));
      setShowFaqs((prev) => prev.filter((faq) => faq.faq_id !== faqToDelete));

      setDeleteConfirmOpen(false);
      setFaqToDelete(null);
      toast.success("FAQ removed from show");
    } catch (error) {
      console.error("Error removing FAQ:", error);
      toast.error("Failed to remove FAQ");
    }
  };

  const getFilteredFAQs = () => {
    return showFaqs.filter((faq) => {
      const venueFaq = venueFaqs.find((v) => v.faq_id === faq.faq_id);
      // Only show FAQs that have content in the selected language (no fallback)
      return (
        venueFaq?.questions[activeLanguage] && venueFaq?.answers[activeLanguage]
      );
    });
  };

  const getSelectOptions = () => {
    return venueFaqs
      .filter((faq) => {
        // Only show FAQs that have content in the selected language
        return (
          faq.status.name === "active" &&
          faq.questions[activeLanguage] &&
          faq.answers[activeLanguage]
        );
      })
      .map((faq) => ({
        value: faq.faq_id.toString(),
        label: faq.questions[activeLanguage] || "Untitled FAQ",
      }));
  };

  const getLanguageDisplay = (lang: string) => {
    const languageMap: Record<string, { flag: string; name: string }> = {
      en: { flag: "🇺🇸", name: "English" },
      sv: { flag: "🇸🇪", name: "Swedish" },
      fr: { flag: "🇫🇷", name: "French" },
      de: { flag: "🇩🇪", name: "German" },
      es: { flag: "🇪🇸", name: "Spanish" },
      it: { flag: "🇮🇹", name: "Italian" },
      no: { flag: "🇳🇴", name: "Norwegian" },
      da: { flag: "🇩🇰", name: "Danish" },
      fi: { flag: "🇫🇮", name: "Finnish" },
    };

    return languageMap[lang] || { flag: "🌐", name: lang.toUpperCase() };
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3"></div>
          <div className="h-32 bg-gray-200 rounded"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold">Show FAQs</h3>
        <p className="text-sm text-muted-foreground">
          Select and configure FAQs from your venue's FAQ bank for this show
        </p>
      </div>

      {/* Language Tabs */}
      <div className="border-b">
        <nav className="-mb-px flex space-x-8 flex-wrap">
          {availableLanguages.map((langCode, index) => {
            const language = AVAILABLE_LANGUAGES.find(
              (l) => l.code === langCode,
            );
            return (
              <div key={langCode} className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setActiveLanguage(langCode)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeLanguage === langCode
                      ? "border-purple-600 text-purple-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <span className="flex items-center gap-2">
                    {language?.flag} {language?.name || langCode.toUpperCase()}
                    {index === 0 && (
                      <Badge variant="secondary" className="text-xs ml-1">
                        Primary
                      </Badge>
                    )}
                  </span>
                </button>
              </div>
            );
          })}
        </nav>
      </div>

      <div className="space-y-6 mt-6">
        {/* FAQ Selection */}
        <Card className="p-6">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <h4 className="font-medium">Select FAQs</h4>
                <p className="text-sm text-muted-foreground">
                  Choose which FAQs from your venue bank to include in this show
                </p>
              </div>
              <Badge variant="secondary">
                {
                  selectedFaqIds.filter((id) => {
                    const faq = venueFaqs.find((f) => f.faq_id === id);
                    return (
                      faq?.questions[activeLanguage] &&
                      faq?.answers[activeLanguage]
                    );
                  }).length
                }{" "}
                selected in {activeLanguage.toUpperCase()}
              </Badge>
            </div>

            <MultiSelectEnhanced
              options={getSelectOptions()}
              selected={selectedFaqIds
                .filter((id) => {
                  // Only show as selected if FAQ has content in current language
                  const faq = venueFaqs.find((f) => f.faq_id === id);
                  return (
                    faq?.questions[activeLanguage] &&
                    faq?.answers[activeLanguage]
                  );
                })
                .map((id) => id.toString())}
              onChange={(values) =>
                handleFAQSelection(values.map((v) => parseInt(v)))
              }
              placeholder="Select FAQs..."
              className="w-full"
            />
          </div>
        </Card>

        {/* FAQ Configuration */}
        <Card className="p-6">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div>
                <h4 className="font-medium">FAQ configuration</h4>
                <p className="text-sm text-muted-foreground">
                  Configure visibility and settings for each selected FAQ
                </p>
              </div>
            </div>

            {getFilteredFAQs().length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground mb-4">
                  No FAQs selected for this show in{" "}
                  {activeLanguage.toUpperCase()}
                </p>
                <p className="text-sm text-muted-foreground">
                  Select FAQs from the venue bank above to configure them for
                  this show
                </p>
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-1/2">
                        Question ({activeLanguage.toUpperCase()})
                      </TableHead>
                      <TableHead className="w-1/3">Visibility</TableHead>
                      <TableHead className="w-20 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {getFilteredFAQs().map((showFaq) => {
                      const venueFaq = venueFaqs.find(
                        (v) => v.faq_id === showFaq.faq_id,
                      );
                      const question =
                        venueFaq?.questions[activeLanguage] || "Untitled FAQ";

                      return (
                        <TableRow key={showFaq.faq_id}>
                          <TableCell className="font-medium">
                            <div className="pr-4 break-words">
                              {question.length > 80
                                ? question.substring(0, 80) + "..."
                                : question}
                            </div>
                          </TableCell>
                          <TableCell>
                            <FaqVisibilitySelect
                              value={
                                showFaq.visibilities?.[activeLanguage] ??
                                showFaq.visibility
                              }
                              onChange={(value) =>
                                handleVisibilityChange(showFaq.faq_id, value)
                              }
                            />
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteClick(showFaq.faq_id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove FAQ from show</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove this FAQ from the show? This will
              not delete the FAQ from your venue bank, only remove it from this
              specific show.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleConfirmDelete}>
              Remove FAQ
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
